import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

import { Observable, Subject, map, takeUntil, tap, forkJoin, timer, switchMap, firstValueFrom, mergeMap } from 'rxjs';
import { ConversationModel, ParticipantModel } from 'src/app/models/conversation.model';
import { AssetService } from 'src/app/services/asset.service';
import { ChatService } from 'src/app/services/chat.service';
import { StudentPortalService } from 'src/app/services/student-portal.service';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
@Component({
  selector: 'app-chat-box',
  templateUrl: './chat-box.component.html',
  styleUrls: ['./chat-box.component.scss']
})
export class ChatBoxComponent implements OnInit {

  private destroyed$ = new Subject();

  messageForm = new FormGroup({
    messageInput: new FormControl({value: "", disabled: false}, Validators.required),
  })

  messageData: ConversationModel;
  studentId: number;
  activeYouthWorkerData: any;
  studentAvatar: any;
  hasAvatar: boolean = false;
  avatarId: any;

  oldMessageData: any;

  refreshing: boolean = false;

  constructor(private _chatService: ChatService, private _snackbar: MatSnackBar,
		private _translator: TranslateService, private _asset: AssetService, private _studentPortalService: StudentPortalService) {  }

  async ngOnInit() {

    this._chatService.getConversationData().pipe(
      // it is now important to unsubscribe from the subject
      takeUntil(this.destroyed$)
    ).subscribe(data => {
      this.messageData = data;
      this.studentId = data.studentUserId;
      this.activeYouthWorkerData = data.participants[data.participants.length - 1];
      this.avatarId = data.participants[data.participants.length - 1].avatarBase64;
      // console.log("convo data: ", data);
    })

    timer(0, 120000).pipe(
      takeUntil(this.destroyed$),
      tap(() => this.refreshing = true),
      switchMap(() => this._chatService.refreshConversationData())).subscribe(
        () => {
          console.log("New data retrieved.");
          this.refreshing = false;
        }
      )
      if(!this.hasAvatar && this.studentAvatar == null)
        this.getStudentAvatar();

      this._chatService.markMessageAsRead();
  }


  getParticipant(id: number): Observable<ParticipantModel> {
    return this._chatService.getParticipant(id).pipe(
      takeUntil(this.destroyed$))
  }

  getStudentAvatar()
  {
    this._studentPortalService.getStudentAvatar().pipe(
      takeUntil(this.destroyed$))
      .subscribe((blob) => {
        const url = URL.createObjectURL(blob);
        this.studentAvatar = url;
        this.hasAvatar = true;
      }, err => {
        console.log("Error fetching student avatar. ", err);
        this.hasAvatar = false;
      }) 
  }

  onSubmit() {
    const messageModel = {
      conversationId: this.messageData.conversationId,
      textContent: this.messageForm.value.messageInput
    }

    this._chatService.sendMessage(messageModel).pipe(
      takeUntil(this.destroyed$),
      mergeMap(() => {
        console.log("Message sent.");
        this.messageForm.reset();
        return this._chatService.refreshConversationData()
      }))
      .subscribe(() => {
        console.log("New conversation data retrieved.")
      })
      
    }

  onAttachFile(event: any) {
    const file = event.target.files[0];
    if(!file)
      return;
    if (!(this.isTypeValid(file.type) && this.isSizeValid(file.size))) {
			return;
		}
    const messageModel = {
      conversationId: this.messageData.conversationId,
      messageAsset: {
        fileName: file.name,
        fileExtension: file.name.split('.')[1],
        binaryContentBase64: "",
        binaryContentType: file.type,
      }
      
    }

    this._asset.blobToBase64(file).pipe(
      takeUntil(this.destroyed$),
      mergeMap(file => {
        messageModel.messageAsset.binaryContentBase64 = file.split(',')[1];
        return this._chatService.sendMessage(messageModel)
      }),
      mergeMap(() => {
        return this._chatService.refreshConversationData();
      })
    ).subscribe(() => {
      console.log("New data retrieved.");
      this.messageForm.reset()
    })
  }

  private isSizeValid(size: number) {
		if (size > MAX_FILE_SIZE) {
			this._snackbar.open(
				this._translator.instant(
					'The file you chose is too large. Please upload a file that is less than 2MB'
				),
				this._translator.instant('Dismiss')
			);
			return false;
		}
		return true;
	}

  private isTypeValid(type: string) {
		const isAcceptedFileType = this.checkIfCorrectFileType(type);
		let isFileValid = true;
		if (!isAcceptedFileType) {
			this._snackbar.open(
				this._translator.instant(
					'The file you chose is the wrong type.'
				),
				this._translator.instant('Dismiss')
			);
			isFileValid = false;
		}
		return isFileValid;
	}

	checkIfCorrectFileType(type: string) {
		const approvedFileTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'
    ];

		if (approvedFileTypes.includes(type)) {
			return true;
		} else {
			return false;
		}
	}

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

}
